import type { DutyRequestStatus } from "@dutyon/apis";
import { findAllDutyRequests, queryKeys } from "@dutyon/apis";
import {
  Card,
  DatePickerInput,
  Group,
  Loader,
  LoadingOverlay,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TitleContainer,
} from "@dutyon/ui-kit";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { DutyRequestTable } from "../../components/duty/DutyRequestTable";

export function DutyRequestListPage() {
  const [activePage, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<DutyRequestStatus | "ALL">("ALL");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  // 날짜 데이터 문자열 포맷팅 (YYYY-MM-DD)
  const startDateStr = startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined;
  const endDateStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined;

  const { data, isPending, isFetching } = useQuery({
    queryKey: queryKeys.dutyRequests.list({
      page: activePage,
      limit,
      status: statusFilter,
      from: startDateStr,
      to: endDateStr,
    }),
    queryFn: () =>
      findAllDutyRequests({
        page: activePage,
        limit,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        startDate: startDateStr,
        endDate: endDateStr,
      }),
    placeholderData: keepPreviousData,
  });

  const list = data?.list || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <TitleContainer
      title="근무 요청"
      rightSection={
        <SegmentedControl
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val as DutyRequestStatus | "ALL");
            setPage(1); // 필터 변경 시 페이지 리셋
          }}
          data={[
            { label: "전체", value: "ALL" },
            { label: "대기중", value: "PENDING" },
            { label: "승인됨", value: "APPROVED" },
            { label: "거절됨", value: "REJECTED" },
            { label: "취소됨", value: "CANCELED" },
          ]}
          color="blue"
        />
      }
      pos="relative"
    >
      <LoadingOverlay visible={isPending} overlayProps={{ blur: 2 }} />
      <Stack gap="lg">
        {/* 기간 필터 컨트롤 패널 */}
        <Card
          withBorder
          padding="md"
          radius="md"
          style={{ backgroundColor: "var(--mantine-color-gray-0)" }}
        >
          <Group align="flex-end" gap="md">
            <DatePickerInput
              type="range"
              label="근무 기간 필터"
              placeholder="기간 선택 (시작일 ~ 종료일)"
              value={dateRange}
              onChange={(val) => {
                setDateRange(val as [Date | null, Date | null]);
                setPage(1); // 필터 변경 시 페이지 리셋
              }}
              clearable
              valueFormat="YYYY-MM-DD"
              style={{ width: 280 }}
            />
            {(startDate || endDate) && (
              <Text
                size="sm"
                c="blue"
                style={{ cursor: "pointer", paddingBottom: 8 }}
                onClick={() => {
                  setDateRange([null, null]);
                  setPage(1);
                }}
              >
                필터 초기화
              </Text>
            )}
          </Group>
        </Card>

        {isPending ? (
          <Card withBorder padding="xl" radius="md">
            <Stack align="center" gap="xs" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                근무 요청 목록을 불러오는 중입니다...
              </Text>
            </Stack>
          </Card>
        ) : list.length === 0 ? (
          <Card withBorder padding="xl" radius="md">
            <Text c="dimmed" ta="center" py="xl">
              근무 요청 데이터가 없습니다.
            </Text>
          </Card>
        ) : (
          <Card withBorder padding={0} radius="md" style={{ overflow: "hidden" }}>
            <DutyRequestTable list={list} />
          </Card>
        )}

        <Group justify="space-between" mt="md">
          <Select
            value={limit.toString()}
            onChange={(val) => {
              setLimit(Number(val));
              setPage(1);
            }}
            data={["10", "20", "50", "100"]}
            style={{ width: 80 }}
            allowDeselect={false}
          />
          {totalPages > 1 && (
            <Pagination total={totalPages} value={activePage} onChange={setPage} />
          )}
          <div style={{ width: 80 }} />
        </Group>
      </Stack>

      <LoadingOverlay visible={isFetching} />
    </TitleContainer>
  );
}
